import { useTranslation } from 'react-i18next'
import { useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { Button } from '../common/Button'
import { useToast } from '../common/Toast'

interface VacationPause {
  _id: Id<'vacationPauses'>
  startDate: string
  endDate: string
}

interface VacationPauseBannerProps {
  pause: VacationPause
}

export function VacationPauseBanner({ pause }: VacationPauseBannerProps) {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const cancelPause = useMutation(api.vacationPauses.cancel)

  const today = new Date()
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

  const isUpcoming = pause.startDate > todayStr
  const isActive = pause.startDate <= todayStr && pause.endDate >= todayStr

  const formatDisplayDate = (dateStr: string): string => {
    const [y, m, d] = dateStr.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    })
  }

  const handleCancel = async () => {
    try {
      const result = await cancelPause({ pauseId: pause._id })
      if (result.success) {
        showToast({
          message: t('medicationManagement.toast.pauseCancelled'),
          type: 'success',
        })
      } else {
        showToast({
          message: result.error || t('errors.generic'),
          type: 'error',
        })
      }
    } catch {
      showToast({ message: t('errors.generic'), type: 'error' })
    }
  }

  const bannerStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.75rem 1rem',
    backgroundColor: isActive ? '#fef3c7' : '#dbeafe',
    border: `1px solid ${isActive ? '#fcd34d' : '#93c5fd'}`,
    borderRadius: '8px',
    marginBottom: '1rem',
    gap: '0.75rem',
    flexWrap: 'wrap',
  }

  const iconTextStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '14px',
    color: isActive ? '#92400e' : '#1e40af',
    fontWeight: 500,
  }

  const messageText = isUpcoming
    ? t('medicationManagement.vacationPause.callsWillPause', {
        start: formatDisplayDate(pause.startDate),
        end: formatDisplayDate(pause.endDate),
      })
    : t('medicationManagement.vacationPause.callsPaused', {
        start: formatDisplayDate(pause.startDate),
        end: formatDisplayDate(pause.endDate),
      })

  return (
    <div style={bannerStyles}>
      <div style={iconTextStyles}>
        <span>{isActive ? '\u23F8' : '\u{1F4C5}'}</span>
        <span>{messageText}</span>
      </div>
      <Button size="small" variant="outline" onClick={handleCancel}>
        {t('medicationManagement.vacationPause.cancelPause')}
      </Button>
    </div>
  )
}
